import React from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableSortLabel,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";

export const EditAccountSortableHeader = ({
  field,
  orderBy,
  order,
  onSort,
  children,
  align = "center",
  headerCellSx,
}) => (
  <TableCell
    align={align}
    sx={{
      ...headerCellSx,
      textAlign: align,
      ...(align === "left" ? { pl: 2 } : null),
    }}
  >
    <TableSortLabel
      active={orderBy === field}
      direction={orderBy === field ? order : "asc"}
      hideSortIcon={orderBy !== field}
      onClick={() => onSort(field)}
      sx={{
        color: "inherit !important",
        display: "inline-flex",
        justifyContent: align === "left" ? "flex-start" : "center",
        alignItems: "center",
        maxWidth: "100%",
        whiteSpace: "nowrap",
        "& .MuiTableSortLabel-icon": {
          fontSize: 14,
          color: "#94a3b8",
          ml: 0.25,
        },
        "&.Mui-active": { color: "#64748b !important" },
      }}
    >
      {children}
    </TableSortLabel>
  </TableCell>
);

export const EditAccountTablePagination = ({
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 20],
}) => {
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage) || 1);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        px: 2,
        py: 1,
        borderTop: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        minHeight: 48,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ color: "#64748b", fontSize: "0.75rem" }}>
          Rows per page
        </Typography>
        <Select
          size="small"
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          sx={{
            fontSize: "0.75rem",
            height: 28,
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
          }}
        >
          {rowsPerPageOptions.map((n) => (
            <MenuItem key={n} value={n} sx={{ fontSize: "0.8rem" }}>
              {n}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ color: "#64748b", fontSize: "0.75rem" }}>
          {total > 0
            ? `${page * rowsPerPage + 1}–${Math.min(
                (page + 1) * rowsPerPage,
                total
              )} of ${total}`
            : "0 of 0"}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onPageChange(0)}
          disabled={page === 0}
          sx={{ color: page === 0 ? "#d1d5db" : "#64748b" }}
        >
          <FirstPageIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          sx={{ color: page === 0 ? "#d1d5db" : "#64748b" }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
          disabled={page >= pageCount - 1 || total === 0}
          sx={{
            color:
              page >= pageCount - 1 || total === 0 ? "#d1d5db" : "#64748b",
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onPageChange(Math.max(0, pageCount - 1))}
          disabled={page >= pageCount - 1 || total === 0}
          sx={{
            color:
              page >= pageCount - 1 || total === 0 ? "#d1d5db" : "#64748b",
          }}
        >
          <LastPageIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export const getDateSortValue = (dateInput) => {
  if (!dateInput || dateInput === "null") return 0;
  if (typeof dateInput === "object" && dateInput.date) {
    const { year, month, day } = dateInput.date;
    return new Date(year, month - 1, day).getTime() || 0;
  }
  const dt = new Date(dateInput);
  return isNaN(dt.getTime()) ? 0 : dt.getTime();
};

export const toggleSort = (orderBy, order, field, setOrderBy, setOrder) => {
  if (orderBy === field) {
    setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  } else {
    setOrderBy(field);
    setOrder("asc");
  }
};
